import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import TireRepair from "@mui/icons-material/TireRepair";
import SellIcon from "@mui/icons-material/Sell";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ReusableDataGrid from "../Components/ReusableDataGrid";

const toLocalDateInput = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getMonthBounds = (value = new Date()) => {
  const date = new Date(value);
  return {
    start: toLocalDateInput(new Date(date.getFullYear(), date.getMonth(), 1)),
    end: toLocalDateInput(new Date(date.getFullYear(), date.getMonth() + 1, 0)),
  };
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPieces = (value) => {
  const count = Number(value || 0);
  return `${count.toLocaleString("en-US")} ${count === 1 ? "piece" : "pieces"}`;
};

function TireAnalysisReport() {
  const navigate = useNavigate();
  const initialRange = getMonthBounds();
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [report, setReport] = useState({
    summary: {},
    sections: {
      new: [],
      used: [],
    },
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const getReport = useCallback(() => {
    setLoading(true);
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    const query = `?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(
      endDate,
    )}&tzOffsetMinutes=${tzOffsetMinutes}`;

    axios
      .get(
        `${Helpers.baseUrl}/reports/tires/${Helpers.authParentId}${query}`,
        Helpers.headers,
      )
      .then((response) => {
        setReport({
          summary: response.data?.summary || {},
          sections: {
            new: Array.isArray(response.data?.sections?.new)
              ? response.data.sections.new
              : [],
            used: Array.isArray(response.data?.sections?.used)
              ? response.data.sections.used
              : [],
          },
        });
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [endDate, navigate, startDate]);

  useEffect(() => {
    getReport();
  }, [getReport]);

  const summary = useMemo(() => report.summary || {}, [report.summary]);

  const statCards = useMemo(
    () => [
      {
        title: "Total Sold",
        value: formatPieces(summary.totalPieces),
        secondary: `${Number(summary.invoiceCount || 0).toLocaleString("en-US")} invoices`,
        icon: <SellIcon sx={{ fontSize: 34 }} />,
        color: "#2563eb",
        bg: "rgba(37, 99, 235, 0.10)",
      },
      {
        title: "New Tires",
        value: formatPieces(summary.newPieces),
        secondary: formatCurrency(summary.newRevenue),
        icon: <TireRepair sx={{ fontSize: 34 }} />,
        color: "#059669",
        bg: "rgba(5, 150, 105, 0.10)",
      },
      {
        title: "Used Tires",
        value: formatPieces(summary.usedPieces),
        secondary: formatCurrency(summary.usedRevenue),
        icon: <TireRepair sx={{ fontSize: 34 }} />,
        color: "#7c3aed",
        bg: "rgba(124, 58, 237, 0.10)",
      },
      {
        title: "Revenue",
        value: formatCurrency(summary.totalRevenue),
        secondary: `${Number(summary.lineCount || 0).toLocaleString("en-US")} tire lines`,
        icon: <AttachMoneyIcon sx={{ fontSize: 34 }} />,
        color: "#d97706",
        bg: "rgba(217, 119, 6, 0.10)",
      },
    ],
    [summary],
  );

  const tireColumns = [
    {
      field: "size",
      headerName: "Size",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 700, color: "#111827" }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "quantity",
      headerName: "Sold",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => formatPieces(params.value),
    },
    {
      field: "revenue",
      headerName: "Revenue",
      width: 130,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "brands",
      headerName: "Brand Breakdown",
      flex: 1.5,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        const brands = Array.isArray(params.value) ? params.value : [];
        if (!brands.length) return "-";

        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              py: 0.5,
            }}
          >
            {brands.map((brand) => (
              <Chip
                key={brand.id || brand.brand}
                size="small"
                label={`${brand.brand}: ${formatPieces(brand.quantity)}`}
                sx={{
                  backgroundColor: "#f1f5f9",
                  color: "#334155",
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>
        );
      },
    },
  ];

  const activeSection =
    activeTab === 0
      ? {
          title: "New",
          rows: report.sections.new,
          pieces: summary.newPieces,
          revenue: summary.newRevenue,
          color: "#059669",
        }
      : {
          title: "Used",
          rows: report.sections.used,
          pieces: summary.usedPieces,
          revenue: summary.usedRevenue,
          color: "#7c3aed",
        };

  const renderTireSection = (title, rows, pieces, revenue, color) => (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color,
              backgroundColor: `${color}14`,
            }}
          >
            <TireRepair sx={{ fontSize: 21 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
            {title}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={formatPieces(pieces)}
            size="small"
            sx={{ fontWeight: 700, backgroundColor: `${color}14`, color }}
          />
          <Chip
            label={formatCurrency(revenue)}
            size="small"
            sx={{ fontWeight: 700, backgroundColor: "#f8fafc", color: "#475569" }}
          />
        </Stack>
      </Stack>
      <ReusableDataGrid
        rows={rows}
        loading={loading}
        columns={tireColumns}
        pageSize={10}
        pageSizeOptions={[10, 25, 50]}
        autoHeight
        noRowsMessage={`No ${title.toLowerCase()} sold in this date range`}
      />
    </Box>
  );

  return (
    <>
      <Box
        className="screen-only"
        sx={{
          pt: { xs: 10, sm: 11, md: 12 },
          pb: 3,
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: "100% !important",
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 3,
              background:
                "linear-gradient(135deg, #1d4ed8 0%, #0f766e 55%, #7c3aed 100%)",
              borderRadius: 2,
              color: "white",
            }}
          >
            <Breadcrumbs
              separator={
                <NavigateNextIcon
                  fontSize="small"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                />
              }
              sx={{ mb: 2 }}
            >
              <MuiLink
                component={Link}
                to="/user/dashboard"
                underline="hover"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Home
              </MuiLink>
              <MuiLink
                component={Link}
                to="/user/reports"
                underline="hover"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                Reports
              </MuiLink>
              <Typography sx={{ color: "white", fontWeight: 600 }}>
                Tire Analysis
              </Typography>
            </Breadcrumbs>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <TireRepair sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Tire Analysis Report
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.88)" }}>
                    Tire sizes sold by condition and brand.
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <DateRangeIcon sx={{ color: "#2563eb" }} />
                <Typography sx={{ fontWeight: 700, color: "#344054" }}>
                  Time Period
                </Typography>
              </Stack>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(event) => {
                  const value = event.target.value;
                  setStartDate(value);
                  if (value > endDate) {
                    setEndDate(value);
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                inputProps={{ min: startDate }}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="contained"
                onClick={getReport}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Refresh Report
              </Button>
            </Stack>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statCards.map((card) => (
              <Grid item xs={12} sm={6} lg={3} key={card.title}>
                <Card elevation={0} sx={{ borderRadius: 2, height: "100%" }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                          {card.title}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
                          {card.value}
                        </Typography>
                        <Typography variant="body2" sx={{ color: card.color, fontWeight: 600 }}>
                          {card.secondary}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: card.color,
                          backgroundColor: card.bg,
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(event, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    minHeight: "56px",
                  },
                }}
              >
                <Tab
                  icon={<TireRepair sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label={`New (${Number(summary.newPieces || 0).toLocaleString("en-US")})`}
                />
                <Tab
                  icon={<TireRepair sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label={`Used (${Number(summary.usedPieces || 0).toLocaleString("en-US")})`}
                />
              </Tabs>
            </Box>
            {renderTireSection(
              activeSection.title,
              activeSection.rows,
              activeSection.pieces,
              activeSection.revenue,
              activeSection.color,
            )}
          </Paper>
        </Container>
      </Box>
      <style>{`
        @media print {
          .screen-only {
            padding-top: 0 !important;
          }
        }
      `}</style>
    </>
  );
}

export default TireAnalysisReport;
